/** sessionStorage 归因参数键 */
export const PROMOTER_ATTRIBUTION_SESSION_KEY = 'promoter_attribution_params';
/** 持久化 PartnerStack 支付归因参数键 */
export const PARTNER_STACK_ATTRIBUTION_SESSION_KEY = 'partner_stack_attribution_params';
/** sessionStorage PartnerStack UTM 优惠标记键 */
const PARTNER_STACK_UTM_OFFER_SESSION_KEY = 'partner_stack_utm_offer';

const CHANNEL_CONFIGS = [
  { channel: 'partnerStack', partnerIdParam: 'ps_partner_key', clickIdParam: 'ps_xid' },
  { channel: 'impact', partnerIdParam: 'irpid', clickIdParam: 'im_ref' },
] as const;

export interface PromoterAttribution {
  channel: string;
  partnerId: string;
  clickId: string;
  /** 来源链接包含任意 `utm_*` 参数时延迟注册 */
  deferSignup: boolean;
  /** PartnerStack 来源 UTM */
  utmSource?: string;
  /** PartnerStack 媒介 UTM */
  utmMedium?: string;
}

export interface PromoterRecordPayload {
  promoter_id: string;
  promoter_click_id: string;
  channel: string;
  /** 来源链接包含任意 `utm_*` 参数时为 true，否则为 false */
  defer_signup: boolean;
  /** 是否为新用户完成注册时的归因上报 */
  signup_attribution: boolean;
}

function isPartnerStackUtmAttribution(attribution: PromoterAttribution | null): boolean {
  return attribution?.channel === 'partnerStack' && attribution.deferSignup;
}

function getPromoterAttributionFromSession(): PromoterAttribution | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = sessionStorage.getItem(PROMOTER_ATTRIBUTION_SESSION_KEY);
    if (!value) {
      return null;
    }

    const parsed = JSON.parse(value) as Partial<PromoterAttribution>;
    if (!parsed.channel || !parsed.partnerId || !parsed.clickId) {
      return null;
    }

    return {
      channel: parsed.channel,
      partnerId: parsed.partnerId,
      clickId: parsed.clickId,
      deferSignup: parsed.deferSignup ?? false,
      utmSource: parsed.utmSource,
      utmMedium: parsed.utmMedium,
    };
  } catch {
    return null;
  }
}

/** 判断当前会话是否来自带 UTM 参数的 PartnerStack 链接。 */
export function isPartnerStackUtmOffer(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const current = getPromoterAttributionFromSearchString(window.location.search);
  if (current) {
    return isPartnerStackUtmAttribution(current);
  }

  try {
    if (sessionStorage.getItem(PARTNER_STACK_UTM_OFFER_SESSION_KEY) === 'true') {
      return true;
    }
  } catch {
    return isPartnerStackUtmAttribution(getPromoterAttributionFromSession());
  }

  return isPartnerStackUtmAttribution(getPromoterAttributionFromSession());
}

/** 将专属落地页识别出的 PartnerStack 优惠保留到后续注册与支付流程。 */
export function savePartnerStackOffer() {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(PARTNER_STACK_UTM_OFFER_SESSION_KEY, 'true');
}

/**
 * 从查询字符串解析 PartnerStack / Impact 归因
 */
export function getPromoterAttributionFromSearchString(
  search: string,
): PromoterAttribution | null {
  const params = new URLSearchParams(search);
  const deferSignup = [...params.keys()].some(key => key.toLowerCase().startsWith('utm_'));

  for (const { channel, partnerIdParam, clickIdParam } of CHANNEL_CONFIGS) {
    const partnerId = params.get(partnerIdParam);
    const clickId = params.get(clickIdParam);
    if (partnerId && clickId) {
      return {
        channel,
        partnerId,
        clickId,
        deferSignup,
        utmSource: params.get('utm_source') ?? undefined,
        utmMedium: params.get('utm_medium') ?? undefined,
      };
    }
  }

  return null;
}

/**
 * 当前 URL 检测到推广归因时保存到会话，供登录成功后上报
 */
export function savePromoterAttributionIfDetected(search = typeof window !== 'undefined' ? window.location.search : '') {
  if (typeof window === 'undefined') {
    return;
  }

  const detected = getPromoterAttributionFromSearchString(search);
  if (!detected) {
    return;
  }

  sessionStorage.setItem(PROMOTER_ATTRIBUTION_SESSION_KEY, JSON.stringify(detected));

  if (detected.channel === 'partnerStack') {
    sessionStorage.setItem(PARTNER_STACK_ATTRIBUTION_SESSION_KEY, JSON.stringify(detected));
  } else {
    sessionStorage.removeItem(PARTNER_STACK_ATTRIBUTION_SESSION_KEY);
  }

  if (isPartnerStackUtmAttribution(detected)) {
    sessionStorage.setItem(PARTNER_STACK_UTM_OFFER_SESSION_KEY, 'true');
  } else {
    sessionStorage.removeItem(PARTNER_STACK_UTM_OFFER_SESSION_KEY);
  }
}

/** 清理已上报的推广归因；PartnerStack 优惠标记保留至会话结束。 */
export function clearPromoterAttribution() {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.removeItem(PROMOTER_ATTRIBUTION_SESSION_KEY);
}

/**
 * 合并当前页 search 与额外 query 片段，生成 `?query`
 */
export function buildLoginUrlSearch(currentSearch: string, extraQuery?: string): string {
  const merged = new URLSearchParams(currentSearch);

  if (extraQuery) {
    new URLSearchParams(extraQuery).forEach((value, key) => {
      merged.set(key, value);
    });
  }

  const result = merged.toString();
  return result ? `?${result}` : '';
}

/**
 * 当前 URL 检测到推广归因时上报
 */
export async function recordPromoterIfDetected(
  record: (data: PromoterRecordPayload) => Promise<unknown>,
  signupAttribution = false,
): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  savePromoterAttributionIfDetected();

  const detected = getPromoterAttributionFromSession();
  if (!detected) {
    return;
  }

  try {
    await record({
      promoter_id: detected.partnerId,
      promoter_click_id: detected.clickId,
      channel: detected.channel,
      defer_signup: detected.deferSignup,
      signup_attribution: signupAttribution,
    });
    clearPromoterAttribution();
  } catch {
    // 忽略，下次会话再试
  }
}
